import type { SearchResultV5 } from '@seek/chalice-types';
import axios, { type AxiosResponse, type CancelTokenSource } from 'axios';
import get from 'lodash/get';

import { v5CountUrl, v5PersonalisedCountUrl } from 'src/config';
import {
  createAuthenticatedHttpClient,
  createUnauthenticatedHttpClient,
  withDevAuth,
  withLastKnownSolUserId,
  withServerHeaders,
} from 'src/modules/seek-api-request';
import type { CookieType } from 'src/types/cookie';

import { buildApiHeaders } from './utils/buildApiHeaders';
import { buildApiQuery } from './utils/buildApiQuery';
import { SEARCH_API_TIMEOUT } from './utils/constants';
import { getAuthClientDetails } from './utils/getAuthClientDetails';
import { getJobSearchDarkProdUrl } from './utils/getJobSearchDarkProdUrl';
import type { BaseAPIProps } from './utils/types';

let onlyCountPromise: Promise<number> | undefined,
  onlyCountPromiseSource: CancelTokenSource | undefined;

const getCountUrl = ({
  isAuthenticated = false,
  cookies,
}: {
  isAuthenticated?: boolean;
  cookies: CookieType;
}) => {
  const darkProdSearchUrlCount = getJobSearchDarkProdUrl({
    cookies,
    path: '/count',
  });

  if (darkProdSearchUrlCount) {
    return darkProdSearchUrlCount;
  }

  if (isAuthenticated) {
    return v5PersonalisedCountUrl;
  }
  return v5CountUrl;
};

export const count = ({
  searchParams,
  country,
  zone,
  cookies,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  testHeaders,
  userQueryId,
  userClientId,
  userAgent,
  sessionId,
  locale,
  isAuthenticated,
  serverProps,
  solId,
  newSince,
  isPreviousQueryModified,
  previousIntentSuggestions,
  shouldIncludeQueryHints,
}: BaseAPIProps & {
  userAgent: string;
  userQueryId?: any;
  newSince?: number;
  serverProps?: Record<string, string | undefined>;
  isPreviousQueryModified?: boolean;
  previousIntentSuggestions?: SearchResultV5['intentSuggestions'];
  shouldIncludeQueryHints?: boolean;
}) => {
  if (onlyCountPromise && onlyCountPromiseSource) {
    onlyCountPromiseSource.cancel();
  }

  const { isAuthenticatedClient, useDevAuth } =
    getAuthClientDetails(isAuthenticated);

  const config = {
    retryPolicy: { retries: 0 },
    defaultRequestConfig: {
      headers: {
        ...testHeaders,
        ...withLastKnownSolUserId(cookies['last-known-sol-user-id']),
        ...(useDevAuth ? withDevAuth(cookies.AUTH_TOKEN) : {}),
      },
    },
  };

  const httpClient = isAuthenticatedClient
    ? createAuthenticatedHttpClient(config)
    : createUnauthenticatedHttpClient(config);

  const isSQCEnabled =
    (cookies['feature_experiment-search-query-correction'] !== 'control' &&
      typeof isPreviousQueryModified === 'undefined') ||
    (isPreviousQueryModified &&
      previousIntentSuggestions &&
      previousIntentSuggestions?.length > 0 &&
      previousIntentSuggestions[0].params?.keywords !== searchParams.keywords);

  const countPromise = httpClient
    .send({
      url: getCountUrl({ isAuthenticated, cookies }),
      timeout,
      cancelToken: get(onlyCountPromiseSource, 'token'),
      label: 'jobsearch-api-count',
      params: buildApiQuery({
        userClientId,
        sessionId,
        searchQuery: searchParams,
        zone,
        country,
        locale,
        userQueryId,
        solId,
        newSince,
        ...(isSQCEnabled && shouldIncludeQueryHints
          ? /*
             * TODO (Search Query Correction) - :
             * - For Scenario #1 & #2, queryHints param initially will only include 'spellingCorrection'.
             * - Further queryHints values, 'all,companySuggestion' are to be added.
             * - Reference: https://myseek.atlassian.net/browse/DJS-1530
             */
            { queryHints: 'spellingCorrection' }
          : {}),
      }),
      headers: {
        ...buildApiHeaders(requestId),
        ...withServerHeaders({
          userAgent,
          ...serverProps,
        }),
      },
    })
    .then((response: AxiosResponse<number>) => response.data);

  const isClient = ENV.CLIENT;
  if (isClient) {
    onlyCountPromise = countPromise;
    onlyCountPromiseSource = axios.CancelToken.source();
  }

  return countPromise;
};
